import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import moment from 'jalali-moment';
import { toast } from 'react-toastify';

import { _post } from '../../../utils/network';

import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb.component';
import FormField from "../../../components/Form/FormField/FormField.component";
import LoadingButton from '../../../components/LoadingButton/LoadingButton.component';
import ListTable from '../../../components/List/ListTable/ListTable.component';
import Pagination from '../../../components/List/Pagination/Pagination.component';

const ConsultationsList = () => {
	const { t } = useTranslation();
	const breadcrumbItems = [
		{ title: t('dashboard'), link: '/dashboard' },
		{ title: t('consultations'), link: '/consultations-list' },
		{ title: t('list') }
	];

	const [consultations, setConsultations] = useState([]);
	let consultationsListData = {
		head: [t('creation_date'), t('user'), t('category'), t('consultant'), t('user_comment'), t('rate'), t('status'), t('operation')],
		body: consultations.map(consultation => [
			moment(consultation.create_date.substring(0, 10), 'YYYY-MM-DD').locale('fa').format('jYYYY/jMM/jDD'),
			<Link className='' to={`/users-edit/${consultation.user_id_information.user_id}`} target="_blank">
				{consultation.user_id_information.user_fullname}
			</Link>,
			<Link className='' to={`/consultation-categories-edit/${consultation.consultationcategory_id_information.consultationcategory_id}`} target="_blank">
				{consultation.consultationcategory_id_information.consultationcategory_name}
			</Link>,
			<Link className='' to={`/consultants-edit/${consultation.consultant_id_information.consultant_id}`} target="_blank">
				{consultation.consultant_id_information.consultant_name}
			</Link>,
			<span className={"badge rounded-pill p-2 badge-subtle-" + (consultation.comment_status === 0 ? 'secondary' : consultation.comment_status === 1 ? 'primary' : consultation.comment_status === 2 ? 'success' : 'warning')}>
				{consultation.comment_status === 0 ? t('not_sent') : consultation.comment_status === 1 ? t('pending') : consultation.comment_status === 2 ? t('confirmed') : t('failed')}
			</span>,
			<span className={"text-" + ((consultation.user_rate || 5) > 3 ? 'success' : 'danger')}>
				{consultation.user_rate || 5}
			</span>,
			<span className={"badge rounded-pill p-2 badge-subtle-" + (consultation.consultation_state === 0 ? 'success' : consultation.consultation_state === 30 ? 'warning' : consultation.consultation_state === 1 ? 'primary' : consultation.consultation_state === 10 ? 'danger' : 'secondary')}>
				{consultation.consultation_state === 0 ? t('pending') : consultation.consultation_state === 30 ? t('canceled') : consultation.consultation_state === 1 ? t('confirmed') : consultation.consultation_state === 10 ? t('failed') : t('closed')}
			</span>,
			<Link className='btn btn-secondary btn-sm p-1' to={`/consultations-edit/${consultation.consultation_id}`}>
				<i className="fa fa-edit"></i> {t('edit')}
			</Link>
		]),
		directions: ['ltr', 'rtl', 'rtl', 'rtl', '', 'ltr', '', 'ltr']
	};
	
	const [pageNumber, setPageNumber] = useState(1);
	const [pageCounter] = useState(10);
	const [totalCount, setTotalCount] = useState(0);
	const [pagesCount, setPagesCount] = useState(0);
	const [searchConsultationCategory, setSearchConsultationCategory] = useState(null);
	const [searchConsultationConsultant, setSearchConsultationConsultant] = useState(null);
	const [searchConsultationStatus, setSearchConsultationStatus] = useState(null);
	const [searchConsultationCommentStatus, setSearchConsultationCommentStatus] = useState(null);
	const [searchButtonIsLoading, setSearchButtonIsLoading] = useState(false);
	const [searchCounter, setSearchCounter] = useState(0);
	const getConsultations = async () => {
		let body = {
			consultationcategory_id: searchConsultationCategory,
			consultant_id: searchConsultationConsultant,
			consultation_state: searchConsultationStatus,
			comment_status: searchConsultationCommentStatus,
			page: pageNumber,
			count: pageCounter
		};
		setSearchButtonIsLoading(true);
		let response = await _post('/consultations/admin/getAll', body);
		setSearchButtonIsLoading(false);
		switch(response.code){
			case 200:
				setTotalCount(response.data.all_consultations);
				setConsultations(response.data.consultations);
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};
	
	const filterConsultations = event => {
		event.preventDefault();
		if(searchButtonIsLoading)
			return 0;
		let categoryID = event.target.searchConsultationCategory.value.trim() || null;
		let consultantID = event.target.searchConsultationConsultant.value.trim() || null;
		let status = event.target.searchConsultationStatus.value.trim() || null;
		let commentStatus = event.target.searchConsultationCommentStatus.value.trim() || null;
		setSearchConsultationCategory(categoryID);
		setSearchConsultationConsultant(consultantID);
		setSearchConsultationStatus(status);
		setSearchConsultationCommentStatus(commentStatus);
		if(pageNumber === 1)
			setSearchCounter(searchCounter + 1);
		else
			setPageNumber(1);
	};

	const [consultationCategories, setConsultationCategories] = useState([]);
	const getConsultationCategories = async () => {
		let body = {
			consultationcategory_name: null,
			page: 1,
			count: 1000000
		};
		let response = await _post('/consultationcategories/admin/getAll', body);
		switch(response.code){
			case 200:
				setConsultationCategories(response.data.consultationcategories.map(item => {
					return {
						key: item.consultationcategory_name,
						value: item.consultationcategory_id
					}
				}));
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};

	const [consultationConsultants, setConsultationConsultants] = useState([]);
	const getConsultationConsultants = async () => {
		let body = {
			consultant_name: null,
			page: 1,
			count: 1000000
		};
		let response = await _post('/consultants/admin/getAll', body);
		switch(response.code){
			case 200:
				setConsultationConsultants(response.data.consultants.map(item => {
					return {
						key: item.consultant_name,
						value: item.consultant_id
					}
				}));
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};

	const consultationStatusList = [
		{ key: t('pending'), value: 0 },
		{ key: t('canceled'), value: 30 },
		{ key: t('confirmed'), value: 1 },
		{ key: t('failed'), value: 10 },
		{ key: t('closed'), value: 20 }
	];

	const consultationCommentStatusList = [
		{ key: t('not_sent'), value: 0 },
		{ key: t('pending'), value: 1 },
		{ key: t('confirmed'), value: 2 },
		{ key: t('failed'), value: 3 }
	];

	useEffect(() => {
		getConsultationCategories();
		getConsultationConsultants();
	}, []);

	useEffect(() => {
		setPagesCount(Math.ceil(totalCount / pageCounter));
	}, [totalCount]);
	
	useEffect(() => {
		setSearchCounter(searchCounter + 1);
	}, [pageNumber]);

	useEffect(() => {
		getConsultations();
	}, [searchCounter]);
	
	return (
		<>
			<Breadcrumb items={breadcrumbItems} />
			<div className="card mb-3">
				<div className="card-header">
					<h5 className="mb-0">{t('search_for')} {t('consultations')}</h5>
				</div>
				<div className="card-body bg-light">
					<form onSubmit={filterConsultations}>
						<div className="row gx-2">
							<FormField type="select" fieldKey="searchConsultationCategory" title={t('category')} value={searchConsultationCategory} selectItems={consultationCategories} isSearch={true} />
							<FormField type="select" fieldKey="searchConsultationConsultant" title={t('consultant')} value={searchConsultationConsultant} selectItems={consultationConsultants} isSearch={true} />
							<FormField type="select" fieldKey="searchConsultationStatus" title={t('status')} value={searchConsultationStatus} selectItems={consultationStatusList} isSearch={true} />
							<FormField type="select" fieldKey="searchConsultationCommentStatus" title={t('comment_status')} value={searchConsultationCommentStatus} selectItems={consultationCommentStatusList} isSearch={true} />
						</div>
						<div>
							<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name="create" isLoading={searchButtonIsLoading}>
								<i className="fa fa-search"></i> {t('search')}
							</LoadingButton>
						</div>
					</form>
				</div>
			</div>
			<ListTable title={t('consultations')} pageNumber={pageNumber} pageCounter={pageCounter} totalCount={totalCount} data={consultationsListData} />
			<Pagination pagesCount={pagesCount} action={setPageNumber} />
		</>
	);
};

export default ConsultationsList;
